import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { isIOS } from '../services/platform.service';
import {
  Heart,
  Sparkles,
  Brain,
  Activity,
  ShieldCheck,
  Users,
  Star,
  Rocket,
  PlayCircle,
  UserCircle,
  ChefHat,
  Dumbbell,
  MapPin,
  BookOpen,
  CalendarCheck,
  Check,
  LayoutDashboard,
  Utensils,
  MessageCircle,
  Target,
  Flame,
  Smartphone,
  Zap,
  TrendingUp,
  Shield,
  Droplets,
  Loader2,
  ChevronDown,
  ChevronUp,
  HelpCircle
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '';

interface SubscriptionPlan {
  id: string;
  tier: string;
  name: string;
  monthlyCredits: number;
  price: number;
  features: string[];
  isPopular: boolean;
  isBestValue: boolean;
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const faqs = [
    {
      category: 'Contul Meu și Utilizare',
      items: [
        {
          q: 'Pe ce dispozitive pot folosi aplicația FII FIT?',
          a: 'FII FIT este disponibilă pe toate platformele majore. O poți descărca pe telefon din App Store (iOS) sau Google Play (Android), ori o poți accesa direct din browserul calculatorului tău pe www.fiifit.ro. Contul tău se sincronizează automat în timp real pe toate dispozitivele.',
        },
        {
          q: 'Cum îmi pot șterge contul definitiv?',
          a: 'Dacă decizi să renunți la serviciile noastre, poți face acest lucru din setările contului tău. Mergi la secțiunea „Șterge contul", introdu cuvântul „DELETE" pentru a confirma acțiunea, iar contul și toate datele tale asociate vor fi șterse definitiv și ireversibil din sistemul nostru.',
        },
        {
          q: 'Cum pot contacta echipa de suport dacă am o problemă?',
          a: 'Suntem aici să te ajutăm! Ne poți trimite un mesaj oricând la adresa de email suport@fiifit.ro. Echipa noastră îți va răspunde în cel mai scurt timp pentru a rezolva orice dificultate tehnică.',
        },
      ],
    },
    {
      category: 'Sistemul de Credite AI',
      items: [
        {
          q: 'La ce folosesc creditele în aplicația FII FIT?',
          a: 'Creditele reprezintă „moneda" prin care deblochezi funcționalitățile noastre avansate bazate pe Inteligență Artificială: conversațiile cu asistentul AI (Chat Nutriționist Sportiv și Chat Antrenor Personal), scanarea imaginilor cu mâncare pentru analiză nutrițională, generarea planurilor alimentare și de antrenament personalizate, precum și generarea listelor inteligente de alimente recomandate pentru obiectivul tău.',
        },

        {
          q: 'Cum obțin credite?',
          a: 'În funcție de planul tău (Gratuit, Basic, Premium sau Gold), contul tău este alimentat automat la începutul fiecărei luni cu un număr fix de credite. Dacă ai nevoie de mai multă asistență AI și îți epuizezi creditele înainte de finalul lunii, poți apăsa butonul „Cumpără Credite Extra" pentru a achiziționa pachete suplimentare sau credite individuale.',
        },
        {
          q: 'Creditele nefolosite se transferă pentru luna următoare?',
          a: 'Nu. Creditele incluse în abonamentul tău lunar se resetează la începutul fiecărui ciclu de facturare (de exemplu, pe data de 1 a fiecărei luni). Acestea nu se raportează pentru luna următoare, așa că te încurajăm să le folosești la maximum pentru obiectivele tale de fitness!',

        },
      ],
    },
    {
      category: 'Abonamente și Plăți',
      items: [
        {
          q: 'Ce planuri de abonament oferă FII FIT?',
          a: 'Oferim soluții pentru fiecare nevoie: Planul Gratuit (10 credite AI bonus lunar, acces la funcții de bază), Planul Basic (€5.99/lună – Chat AI, planuri alimentare 7 zile, scanare alimente), Planul Premium (€9.99/lună – analitică avansată, export PDF/CSV, suport prioritar) și Planul Gold (€19.99/lună – toate funcțiile, consultanță 1-la-1 și suport VIP 24/7).',
        },
        {
          q: 'Cum plătesc și care este perioada de facturare?',
          a: 'Facturarea se face lunar. Poți achita comod și sigur direct din aplicația de pe telefon (prin Apple Pay sau Google Pay) sau direct pe platforma web.',
        },
        {
          q: 'Pot schimba sau anula abonamentul oricând?',
          a: 'Da, ai control total. Poți face upgrade (trecere la un plan superior) sau downgrade oricând dorești, din secțiunea „Gestionare Facturare". Dacă dorești să anulezi abonamentul, o poți face direct din aplicație sau din setările abonamentelor de pe telefonul tău, iar beneficiile Premium vor rămâne active până la finalul lunii deja plătite.',
        },
      ],
    },
  ];

  // Redirect to dashboard if user is logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    } else if (isIOS()) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Load subscription plans from API
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/subscriptions/plans`);
        setPlans(res.data.plans || []);
      } catch (error) {
        console.error('Error loading plans:', error);
      } finally {
        setPlansLoading(false);
      }
    };
    loadPlans();
  }, []);

  // RON price mapping
  const ronPrices: Record<string, string> = {
    'FREE': '',
    'BASIC': '29.99',
    'PREMIUM': '49.99',
    'UNLIMITED': '99.99',
  };

  const planStyles = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return {
          border: 'border-gray-200',
          badge: 'bg-gray-100 text-gray-700',
          btn: 'bg-gray-900 hover:bg-gray-800',
          topBadge: 'bg-green-500',
          topBadgeText: '✓ ACTIV',
          creditsBg: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200',
          creditsText: 'text-green-700',
          creditsNumber: 'text-green-600',
        };
      case 'BASIC':
        return {
          border: 'border-blue-200',
          badge: 'bg-blue-100 text-blue-700',
          btn: 'bg-blue-600 hover:bg-blue-700',
          topBadge: 'bg-blue-600',
          topBadgeText: '⭐ POPULAR',
          creditsBg: 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
          creditsText: 'text-blue-700',
          creditsNumber: 'text-blue-600',
        };
      case 'PREMIUM':
        return {
          border: 'border-purple-300',
          badge: 'bg-purple-100 text-purple-700',
          btn: 'bg-blue-600 hover:bg-blue-700',
          topBadge: 'bg-gradient-to-r from-orange-500 to-yellow-500',
          topBadgeText: '⚡ RECOMANDAT',
          creditsBg: 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200',
          creditsText: 'text-purple-700',
          creditsNumber: 'text-purple-600',
        };
      case 'UNLIMITED':
        return {
          border: 'border-yellow-300',
          badge: 'bg-yellow-100 text-yellow-700',
          btn: 'bg-blue-600 hover:bg-blue-700',
          topBadge: 'bg-gradient-to-r from-amber-500 to-yellow-500',
          topBadgeText: '👑 BEST VALUE',
          creditsBg: 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200',
          creditsText: 'text-amber-700',
          creditsNumber: 'text-amber-600',
        };
      default:
        return {
          border: 'border-gray-200',
          badge: 'bg-gray-100 text-gray-700',
          btn: 'bg-gray-900 hover:bg-gray-800',
          topBadge: 'bg-gray-500',
          topBadgeText: '',
          creditsBg: 'bg-gray-50 border-gray-200',
          creditsText: 'text-gray-700',
          creditsNumber: 'text-gray-600',
        };
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b border-blue-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Dumbbell className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">FII FIT</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-6">
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Funcționalități</a>
              <a href="#modules" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Module</a>
              <a href="#pricing" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">Prețuri</a>
              <a href="#faq" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">FAQ</a>
              <Link to="/business" className="text-teal-600 hover:text-teal-700 transition-colors font-semibold border border-teal-200 px-3 py-1.5 rounded-lg hover:bg-teal-50">
                Pentru Profesioniști
              </Link>
              
              {user ? (
                <>
                  <Link to="/dashboard" className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Link>
                  <Link to="/profile" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg flex items-center gap-1 font-medium">
                    <UserCircle className="w-4 h-4" />
                    Profilul Meu
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Conectare</Link>
                  <Link to="/register" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2.5 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg font-medium">
                    Începe Gratuit
                  </Link>
                </>
              )}
            </div>
            
            <div className="md:hidden flex items-center gap-2">
              {user ? (
                <Link to="/dashboard" className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 font-medium">
                  <LayoutDashboard className="w-3 h-3" />
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-blue-600 text-sm font-medium">Login</Link>
                  <Link to="/register" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                    Începe
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-md">
              <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
              Platforma Fitness #1 pentru Transformare Corporală în România

            </div>
            
            <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 mb-8 leading-tight">
              Forma ta fizică,<br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">personalizată</span> de<br />
              <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">inteligența artificială</span>
            </h1>

            
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-4xl mx-auto leading-relaxed">
              10+ module fitness complete cu AI avansat. Profil personalizat, nutriție adaptată, 
              exerciții specializate, tracking inteligent și comunitate de suport. Totul într-o singură aplicație.
            </p>

            {/* App Store & Google Play Badges */}
            <div className="flex flex-row flex-wrap gap-3 sm:gap-4 justify-center items-center mb-10">
              <a
                href="https://apps.apple.com/ro/app/fii-fit/id6759916689?l=ro"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Descarcă din App Store"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Descarcă din App Store"
                  className="h-10 sm:h-12 w-auto"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=ro.fiifit.app"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Disponibil în Google Play"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Disponibil în Google Play"
                  className="h-[60px] sm:h-[71px] w-auto -my-[10px] sm:-my-[12px]"
                />
              </a>
            </div>


            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">

              <Link 
                to="/register" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center gap-3 text-lg"
              >
                <Rocket className="w-6 h-6" />
                Începe Acum
              </Link>
              <a 
                href="#modules"
                className="border-2 border-gray-300 text-gray-700 px-10 py-5 rounded-2xl font-bold hover:border-blue-600 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center gap-3 text-lg"
              >
                <PlayCircle className="w-6 h-6" />
                Descoperă Modulele
              </a>
            </div>
            
            {/* Trust indicators - Modern Design */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex flex-col items-center">
                  <Shield className="w-8 h-8 text-green-600 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">Date Criptate</p>
                  <p className="text-xs text-gray-500">GDPR Compliant</p>
                </div>
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex flex-col items-center">
                  <Users className="w-8 h-8 text-blue-600 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">1000+</p>
                  <p className="text-xs text-gray-500">Utilizatori Activi</p>
                </div>
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex flex-col items-center">
                  <Star className="w-8 h-8 text-yellow-500 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">4.9/5</p>
                  <p className="text-xs text-gray-500">Rating Mediu</p>
                </div>
              </div>
              
              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-gray-100">
                <div className="flex flex-col items-center">
                  <Brain className="w-8 h-8 text-purple-600 mb-2" />
                  <p className="text-sm font-semibold text-gray-900">Antrenor AI</p>
                  <p className="text-xs text-gray-500">AI Avansat</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Background decoration - Enhanced */}
        <div className="absolute top-0 right-0 -z-10 opacity-30">
          <div className="w-96 h-96 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-3xl"></div>
        </div>
        <div className="absolute bottom-0 left-0 -z-10 opacity-30">
          <div className="w-96 h-96 bg-gradient-to-br from-green-400 to-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 -z-10 opacity-20">
          <div className="w-[600px] h-[600px] bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Features Section - 3 Features Principale */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              De ce FII FIT este diferit?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Tehnologie avansată AI, personalizare totală și suport 24/7 pentru călătoria ta spre cea mai bună formă fizică
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-10">
            {/* Feature 1 - Antrenor AI */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-400 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-blue-50 to-purple-50 p-10 rounded-3xl hover:shadow-2xl transition-all border-2 border-blue-100 group-hover:border-blue-300">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg group-hover:scale-110 transition-transform">
                  <Brain className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Antrenor AI Avansat</h3>
                <p className="text-gray-600 leading-relaxed text-center mb-6">
                  Antrenor AI personal antrenat pentru obiective de fitness și transformare corporală. Înțelege nevoile tale, 
                  recomandă antrenamente și răspunde instant la întrebările tale despre fitness și nutriție.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Recomandări personalizate bazate pe profil</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Coach AI disponibil 24/7</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Insights și pattern-uri din datele tale</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 2 - Food Vision AI */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-blue-400 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-green-50 to-blue-50 p-10 rounded-3xl hover:shadow-2xl transition-all border-2 border-green-100 group-hover:border-green-300">
                <div className="w-20 h-20 bg-gradient-to-br from-green-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg group-hover:scale-110 transition-transform">
                  <Utensils className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Food Vision AI</h3>
                <p className="text-gray-600 leading-relaxed text-center mb-6">
                  Fotografiază mâncarea cu camera și AI-ul recunoaște automat alimentele. 
                  Analiză nutrițională completă, calorii, macronutrienți și tracking instant.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Recunoaștere automată alimente</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Calculare instant calorii & nutrienți</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Tracking grafic al progresului</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 3 - Planuri Personalizate */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 p-10 rounded-3xl hover:shadow-2xl transition-all border-2 border-purple-100 group-hover:border-purple-300">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg group-hover:scale-110 transition-transform">
                  <Target className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Planuri 100% Personalizate</h3>
                <p className="text-gray-600 leading-relaxed text-center mb-6">
                  Adaptat la 2 moduri de viață (Sănătos & Activ / Recuperare) cu 25+ obiective de fitness. 
                  Planuri nutriție, antrenamente și rutine zilnice create special pentru tine.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>25+ obiective de fitness disponibile</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Planuri mese 7 zile cu rețete</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
                    <span>Antrenamente adaptate la nivelul tău</span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section - 10+ Module Complete */}
      <section id="modules" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              10+ Module Complete Pentru Tine
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Tot ce ai nevoie pentru a-ți atinge obiectivele de fitness într-o singură aplicație modernă
            </p>

          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Module 1 - Profil Fitness */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-blue-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <UserCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Profil Fitness</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Profil complet personalizat cu obiectiv principal de fitness, nivel actual, preferințe și istoric de antrenament. 
                Date criptate GDPR pentru siguranță maximă.
              </p>
              <div className="flex items-center text-sm text-blue-600 font-medium">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Date securizate & criptate
              </div>
            </div>
            
            {/* Module 2 - Nutriție AI */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-green-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <ChefHat className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Nutriție AI</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Planuri de mese personalizate 7 zile, rețete video și liste de cumpărături. 
                Adaptate perfect la obiectivul tău de fitness.
              </p>

              <div className="flex items-center text-sm text-green-600 font-medium">
                <Brain className="w-4 h-4 mr-2" />
                Planuri AI personalizate
              </div>
            </div>
            
            {/* Module 3 - Exerciții Video */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-purple-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <Dumbbell className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Exerciții Video</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                100+ video-uri de antrenamente adaptate pe 3 nivele: începător/intermediar/avansat. 
                Filtre pentru sală, acasă, parc, gantere sau bandă elastică.
              </p>

              <div className="flex items-center text-sm text-purple-600 font-medium">
                <PlayCircle className="w-4 h-4 mr-2" />
                100+ video-uri HD
              </div>
            </div>
            
            {/* Module 4 - Chat AI Antrenor */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-indigo-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Chat AI Antrenor</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Asistent AI disponibil 24/7 pentru întrebări despre antrenamente, nutriție sportivă și recuperare. 
                Răspunsuri instant.
              </p>

              <div className="flex items-center text-sm text-indigo-600 font-medium">
                <Zap className="w-4 h-4 mr-2" />
                Disponibil 24/7
              </div>
            </div>
            
            {/* Module 5 - Specialiști */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-red-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Hartă Specialiști</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Găsește specialiști în zona ta: antrenori personali, nutriționiști sportivi, kinetoterapeuți. 
                Review-uri verificate.
              </p>
              <div className="flex items-center text-sm text-red-600 font-medium">
                <Star className="w-4 h-4 mr-2" />
                Review-uri reale
              </div>
            </div>
            
            {/* Module 6 - Calendar & Tracking */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-pink-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <CalendarCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Calendar & Tracking</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Urmărește mese, antrenamente și progres în calendar interactiv. 
                Export rapoarte PDF de progres.
              </p>
              <div className="flex items-center text-sm text-pink-600 font-medium">
                <Activity className="w-4 h-4 mr-2" />
                Rapoarte PDF
              </div>
            </div>
            
            {/* Module 7 - Food Tracking AI Vision */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-amber-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <Utensils className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Food Vision AI</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Fotografiază mâncarea și AI recunoaște automat alimentele. 
                Analiză nutrițională completă instant.
              </p>
              <div className="flex items-center text-sm text-amber-600 font-medium">
                <Smartphone className="w-4 h-4 mr-2" />
                AI Vision avansat
              </div>
            </div>
            
            {/* Module 8 - Hidratare & Aport Apă */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-sky-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <Droplets className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Hidratare & Apă</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Urmărește aportul zilnic de apă cu obiective personalizate în funcție de greutate
                și nivel de activitate. Reminder-uri și streak counter pentru consistență.
              </p>

              <div className="flex items-center text-sm text-sky-600 font-medium">
                <Flame className="w-4 h-4 mr-2" />
                Obiectiv zilnic personalizat
              </div>
            </div>
            
            {/* Module 9 - AI Insights */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-cyan-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">AI Insights</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Pattern-uri inteligente, corelații și predicții bazate pe datele tale. 
                Insights avansate despre progresul tău.
              </p>
              <div className="flex items-center text-sm text-cyan-600 font-medium">
                <Brain className="w-4 h-4 mr-2" />
                Pattern-uri AI
              </div>
            </div>
            
            {/* Module 10 - Educație & Resurse */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-teal-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <BookOpen className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Educație Fitness</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Articole validate, ghiduri video și răspunsuri la întrebările tale despre fitness și nutriție.
              </p>
              <div className="flex items-center text-sm text-teal-600 font-medium">
                <Sparkles className="w-4 h-4 mr-2" />
                Conținut verificat de specialiști
              </div>
            </div>
            
            {/* Module 11 - Comunitate */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 hover:border-violet-300 group">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 ml-4">Comunitate</h3>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Forum de suport, postări, experiențe și sfaturi de la alți pasionați de fitness cu obiective similare.
              </p>

              <div className="flex items-center text-sm text-violet-600 font-medium">
                <Heart className="w-4 h-4 mr-2" />
                Suport mutual
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Dynamic from API (same as /subscriptions) */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              Alege Planul Perfect Pentru Tine
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Acces complet la toate funcționalitățile. Credite FII FIT lunare pentru chat, nutriție, exerciții și mai mult.
            </p>
          </div>

          {plansLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              <span className="ml-3 text-gray-500 text-lg">Se încarcă planurile...</span>
            </div>
          ) : plans.length > 0 ? (
            <div className={`grid gap-8 max-w-7xl mx-auto ${
              plans.length === 1 ? 'md:grid-cols-1 max-w-md' :
              plans.length === 2 ? 'md:grid-cols-2 max-w-3xl' :
              plans.length === 3 ? 'md:grid-cols-3 max-w-5xl' :
              'md:grid-cols-2 lg:grid-cols-4'
            }`}>
              {plans.map((plan) => {
                const style = planStyles(plan.tier);
                const ronPrice = ronPrices[plan.tier] || '';

                return (
                  <div
                    key={plan.id}
                    className={`relative bg-white rounded-3xl overflow-visible transition-all hover:shadow-2xl border-2 ${style.border} shadow-lg`}
                  >
                    {/* Top Badge */}
                    {(plan.isPopular || plan.tier === 'PREMIUM' || plan.isBestValue || plan.tier === 'FREE') && (
                      <div className="absolute -top-4 left-4 z-10">
                        <span className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-md ${style.topBadge}`}>
                          {style.topBadgeText}
                        </span>
                      </div>
                    )}

                    <div className="p-8 pt-10">
                      {/* Tier Badge */}
                      <div className={`inline-block px-3 py-1 rounded-md text-xs font-semibold mb-3 border ${style.badge}`}>
                        {plan.tier === 'UNLIMITED' ? 'GOLD' : plan.tier}
                      </div>

                      {/* Plan Name */}
                      <h3 className="text-2xl font-bold text-gray-900 mb-5">{plan.name}</h3>

                      {/* Price */}
                      <div className="mb-5">
                        {plan.price === 0 ? (
                          <div>
                            <span className="text-4xl font-extrabold text-gray-900">Gratuit</span>
                            <span className="text-gray-500 ml-2 text-sm">fără abonament</span>
                          </div>
                        ) : (
                          <div className="flex items-baseline gap-1 flex-wrap">
                            <span className="text-4xl font-extrabold text-gray-900">
                              €{plan.price.toFixed(2)}
                            </span>
                            <div className="text-gray-500 text-sm">
                              /lună {ronPrice ? `· ${ronPrice} lei` : ''}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Credits Box */}
                      <div className={`mb-6 px-4 py-4 rounded-xl border ${style.creditsBg}`}>
                        <div className={`text-4xl font-extrabold ${style.creditsNumber}`}>{plan.monthlyCredits}</div>
                        <div className={`text-sm font-bold ${style.creditsText}`}>credite FII FIT</div>
                        <div className={`text-xs ${style.creditsText} opacity-75 mt-0.5`}>
                          {plan.tier === 'FREE' ? 'credite FII FIT acordate o singură dată' : 'credite FII FIT pe lună'}
                        </div>
                      </div>

                      {/* Features */}
                      <ul className="space-y-3 mb-8">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* Subscription note */}
                      <div className="mb-3 px-3 py-2 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500">
                          {plan.price > 0 ? 'Abonament lunar' : 'Nu se reînnoiește lunar'}
                        </p>
                      </div>

                      {/* CTA Button */}
                      <Link
                        to="/register"
                        className={`w-full py-4 px-6 rounded-xl font-bold text-white text-center block shadow-lg hover:shadow-xl transition-all transform hover:scale-105 ${style.btn}`}
                      >
                        {plan.price === 0 ? 'Începe Gratuit' : `Alege ${plan.name}`}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Fallback if API fails - show simple CTA */
            <div className="text-center py-12">
              <p className="text-gray-500 mb-6">Planurile se încarcă...</p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                <Rocket className="w-5 h-5" />
                Începe Gratuit
              </Link>
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mt-10">
            Toate planurile includ acces la toate modulele. Diferența este în numărul de credite FII FIT lunare.
            <br />
            Poți schimba sau anula planul oricând din setări.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
            Gata să-ți transformi corpul și stilul de viață?
          </h2>
          <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed">
            Alătură-te comunității de peste 1000 de persoane care și-au atins obiectivele de fitness cu FII FIT. 
            Începe astăzi călătoria ta spre cea mai bună formă fizică din viața ta.
          </p>

          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Link 
              to="/register" 
              className="bg-white text-blue-600 px-12 py-5 rounded-2xl font-bold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-2xl flex items-center gap-3 text-lg"
            >
              <Rocket className="w-6 h-6" />
              Începe Acum Gratuit
            </Link>
            <a 
              href="#pricing"
              className="border-2 border-white text-white px-12 py-5 rounded-2xl font-bold hover:bg-white/10 transition-all flex items-center gap-3 text-lg backdrop-blur-sm"
            >
              <Star className="w-6 h-6" />
              Vezi Prețurile
            </a>
          </div>
          
          {/* Social Proof */}
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/90 text-sm">
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              <span>Date Securizate GDPR</span>
            </div>
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              <span>1000+ Utilizatori Fericiți</span>
            </div>
            <div className="flex items-center">
              <Star className="w-5 h-5 mr-2 text-yellow-300" />
              <span>Rating 4.9/5</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-6 shadow-lg">
              <HelpCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6">
              Întrebări Frecvente
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Răspunsuri la cele mai comune întrebări despre FII FIT
            </p>
          </div>

          <div className="space-y-10">
            {faqs.map((group, groupIndex) => (
              <div key={groupIndex}>
                <h3 className="text-sm font-bold text-blue-700 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <span className="w-8 h-0.5 bg-blue-700 inline-block"></span>
                  {group.category}
                </h3>
                <div className="space-y-3">
                  {group.items.map((item, itemIndex) => {
                    const globalIndex = groupIndex * 10 + itemIndex;
                    const isOpen = expandedFaq === globalIndex;
                    return (
                      <div
                        key={itemIndex}
                        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
                      >
                        <button
                          onClick={() => setExpandedFaq(isOpen ? null : globalIndex)}
                          className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 pr-4">{item.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-500 mb-4">Nu ai găsit răspunsul căutat?</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <MessageCircle className="w-5 h-5" />
              Contactează-ne
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Dumbbell className="w-7 h-7 text-white" />
                </div>
                <span className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">FII FIT</span>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6 max-w-md">
                Platforma de fitness #1 pentru transformare corporală în România. Inteligență artificială avansată, 
                personalizare totală și suport 24/7 pentru obiectivele tale de fitness.
              </p>

              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors">
                  <Users className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors">
                  <Star className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors">
                  <Heart className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            {/* Aplicația */}
            <div>
              <h3 className="font-bold text-lg mb-4">Aplicația</h3>
              <ul className="space-y-3">
                <li><a href="#features" className="text-gray-400 hover:text-white transition-colors">Funcționalități</a></li>
                <li><a href="#modules" className="text-gray-400 hover:text-white transition-colors">Module</a></li>
                <li><a href="#pricing" className="text-gray-400 hover:text-white transition-colors">Prețuri</a></li>
                <li><Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">Demo</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Termeni și Condiții (EULA)</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Politica de Confidențialitate</Link></li>
              </ul>
            </div>
            
            {/* Suport & Legal */}
            <div>
              <h3 className="font-bold text-lg mb-4">Suport & Legal</h3>
              <ul className="space-y-3">
                <li><a href="#faq" className="text-gray-400 hover:text-white transition-colors">Întrebări Frecvente</a></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Termeni & Condiții</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Politică GDPR</Link></li>
                <li>
                  <Link to="/business" className="text-teal-400 hover:text-teal-300 transition-colors font-semibold flex items-center gap-1">
                    💪 FII FIT Business
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="border-t border-gray-800 pt-8 mb-8">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                <strong className="text-gray-300">💪 Notă:</strong> FII FIT este o aplicație de suport informativ și educațional în domeniul fitness și nutriției sportive. Recomandările generate de inteligența artificială au caracter informativ și sunt adaptate obiectivelor tale de fitness. Consultă un specialist înainte de a începe un program intens de antrenament dacă ai condiții fizice speciale.
              </p>
              <p className="text-gray-500 text-xs leading-relaxed">
                SC GONGA SHOP SRL · CUI: RO40071310 · Str. Nicolae Titulescu 16, Agigea, jud. Constanța, România · Tel: 0787-569.002<br />
                Datele dumneavoastră sunt protejate conform GDPR (Regulamentul UE 2016/679).
                Pentru întrebări: <a href="mailto:office@fiifit.ro" className="text-blue-400 hover:text-blue-300">office@fiifit.ro</a>
              </p>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2026 SC GONGA SHOP SRL. Toate drepturile rezervate. | FII FIT | Platformă conformă GDPR.
              </p>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <Link to="/terms" className="hover:text-gray-300 transition-colors">Termeni și Condiții</Link>
                <span>|</span>
                <Link to="/privacy" className="hover:text-gray-300 transition-colors">Politica de Confidențialitate</Link>
                <span>|</span>
                <Link to="/contact" className="hover:text-gray-300 transition-colors">Contact</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
